import { useNow } from '@/hooks/useNow';
import { useMemo } from 'react';

/**
 * useLiveCallState — dérive l'état d'affichage d'un appel en cours à partir des
 * appels actifs Twilio + de la machine XState. Logique extraite de l'ancien
 * CallSheet (supprimé 2026-05-21).
 *
 * Retourne :
 *  - isLive : true quand un humain est confirmé en ligne (connected_at posé).
 *  - pillState : état pour VFCallStatusPill (online/verifying/ringing/...).
 *  - elapsed : durée écoulée en secondes (ou null).
 */

type CallStatus = string | null | undefined;

interface ActiveCallLite {
  id?: string;
  call_status?: CallStatus;
  connected_at?: string | null;
}

interface MachineViewLite {
  actor: unknown;
  stateValue: string | null;
}

function computeElapsed(
  startedAt: number | string | Date | null | undefined,
  currentTime: Date | number | null | undefined,
): number | null {
  if (!startedAt) return null;
  const startMs = typeof startedAt === 'number' ? startedAt : new Date(startedAt).getTime();
  const nowMs = currentTime instanceof Date ? currentTime.getTime() : currentTime || Date.now();
  return Math.max(0, Math.floor((nowMs - startMs) / 1000));
}

export type PillState =
  | 'online' | 'verifying' | 'ringing' | 'dialing' | 'voicemail' | 'hold' | 'ended';

export function useLiveCallState(opts: {
  activeCalls?: ActiveCallLite[];
  callId?: string | null;
  callStartedAt?: number | string | Date | null;
  currentTime?: Date | number | null;
  isOnHold?: boolean;
  machineView?: MachineViewLite | null;
}) {
  const { activeCalls = [], callId, callStartedAt, isOnHold, machineView } = opts;
  // Own clock, only while there is a call to time (2026-10-07).
  const ticking = useNow(1000, !!callStartedAt);
  const currentTime = opts.currentTime ?? ticking;

  const hasTwilioConnectedCall = activeCalls.some(
    (c) => c.call_status === 'connected_to_agent' && c.connected_at,
  );

  const isLive = machineView?.actor
    ? machineView.stateValue === 'connected_to_agent' || machineView.stateValue === 'bridging'
    : hasTwilioConnectedCall;

  const elapsed = useMemo(
    () => computeElapsed(callStartedAt, currentTime),
    [callStartedAt, currentTime],
  );

  const pillState = useMemo<PillState>(() => {
    const currentCall = callId ? activeCalls.find((ac) => ac.id === callId) : null;
    if (!currentCall) return 'ended';
    if (isOnHold && isLive) return 'hold';
    if (currentCall.call_status === 'connected_to_agent' && currentCall.connected_at) return 'online';
    if (currentCall.call_status === 'connected_to_agent') return 'verifying';
    if (currentCall.call_status === 'ringing' || currentCall.call_status === 'human_detected') return 'ringing';
    if (currentCall.call_status === 'voicemail') return 'voicemail';
    return 'dialing';
  }, [activeCalls, callId, isOnHold, isLive]);

  return { isLive, hasTwilioConnectedCall, elapsed, pillState };
}
